import {COLORS} from "./colors-ts";
import type {ThemeOptions} from "@mui/material";
import {createTheme, unstable_createMuiStrictModeTheme} from "@mui/material";
import {environment} from "../env";

// @see: https://material-ui.com/customization/default-theme/
export const theme: ThemeOptions = {
    palette: {...COLORS},
    typography: {
        fontSize: 14,
        h1: {
            fontSize: '6rem',
        },
        h2: {
            // Page title
            fontSize: '3.75rem',
        },
        h3: {
            fontSize: '3rem',
        },
        body1: {
            fontSize: '1rem',
        },
        body2: {
            fontSize: '0.875rem',
        },
    },
    shape: {
        borderRadius: 8,
    },
    shadows: [
        'none', // 0
        '0 1px 3px rgba(0,0,0,0.08)', // 1
        '0 1px 5px rgba(0,0,0,0.09)', // 2
        '0 1px 8px rgba(0,0,0,0.10)', // 3
        '0 1px 10px 0 rgba(0,0,0,0.10), 0 4px 5px 0 rgba(0,0,0,0.10), 0 2px 4px -1px rgba(0,0,0,0.20)', // 4
        '0 2px 12px rgba(0,0,0,0.11), 0 6px 6px rgba(0,0,0,0.10)', // 5
        '0 3px 14px rgba(0,0,0,0.12), 0 8px 8px rgba(0,0,0,0.10)', // 6
        '0 4px 16px rgba(0,0,0,0.12), 0 8px 10px rgba(0,0,0,0.10)', // 7
        '0 5px 18px rgba(0,0,0,0.13), 0 10px 12px rgba(0,0,0,0.10)', // 8
        '0 6px 20px rgba(0,0,0,0.13), 0 12px 14px rgba(0,0,0,0.10)', // 9
        '0 8px 24px rgba(0,0,0,0.14), 0 14px 16px rgba(0,0,0,0.12)', // 10
        '0 10px 28px rgba(0,0,0,0.15)', // 11
        '0 12px 32px rgba(0,0,0,0.15)', // 12
        '0 14px 36px rgba(0,0,0,0.16)', // 13
        '0 16px 40px rgba(0,0,0,0.16)', // 14
        '0 18px 44px rgba(0,0,0,0.17)', // 15
        '0 20px 48px rgba(0,0,0,0.17)', // 16
        '0 22px 52px rgba(0,0,0,0.18)', // 17
        '0 24px 56px rgba(0,0,0,0.18)', // 18
        '0 26px 60px rgba(0,0,0,0.19)', // 19
        '0 28px 64px rgba(0,0,0,0.19)', // 20
        '0 30px 68px rgba(0,0,0,0.20)', // 21
        '0 32px 72px rgba(0,0,0,0.20)', // 22
        '0 34px 76px rgba(0,0,0,0.21)', // 23
        '0 36px 80px rgba(0,0,0,0.22)', // 24
    ],
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '8px',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: '#4088F8',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    minWidth: 0,
                },
                primary: {
                    fontSize: '14px',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
                secondary: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
        },

        MuiList: {
            styleOverrides: {
                root: ({theme}) => ({
                    '&.menuList': {
                        padding: theme.spacing(1),
                        '& .MuiListItemIcon-root': {
                            marginRight: '12px',
                        },
                        '& .MuiListItem-root': {
                            marginTop: theme.spacing(1),
                            marginBottom: theme.spacing(1),
                        },
                        '& .MuiListItemButton-root': {
                            paddingLeft: theme.spacing(1.5),
                            paddingRight: theme.spacing(1.5),
                            paddingTop: theme.spacing(1),
                            paddingBottom: theme.spacing(1),
                            '&:has(.MuiListItemText-root):not(:has(.MuiListItemText-secondary))': {
                                height: '36px',
                            },
                            '&.Mui-selected, &:hover': {
                                borderRadius: '8px',
                            },
                        },
                        '& .MuiListSubheader-root': {
                            fontSize: '12px',
                        },
                    },
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: '8px',
                    boxShadow: '0 1px 10px 0 rgba(0, 0, 0, 0.10), 0 4px 5px 0 rgba(0, 0, 0, 0.10), 0 2px 4px -1px rgba(0, 0, 0, 0.20)',
                },
                list: {
                    padding: '8px 0',
                    '& .MuiListItemIcon-root': {

                        color: COLORS.primary.main,
                        '& svg': {
                            width: '24px',
                            height: '24px',
                        },
                    },
                    '& .MuiButtonBase-root.MuiMenuItem-root .MuiListItemIcon-root': {
                        minWidth: '24px',
                    },
                },
            },
        },
    },
    // spacing = N * 8px (https://material-ui.com/customization/spacing/)
};

// const v4Theme = adaptV4Theme(theme)
export const _theme = environment.production ? createTheme(theme) : unstable_createMuiStrictModeTheme(theme);
